// src/screens/LeaderboardScreen.js
// Public leaderboard — the visible "verified competitive ladder."
// Ranks players per game (and overall) by Crits earned / wins / losses /
// podiums / events entered, for the current season or all-time. Spectator-
// visible (logged-out OK). Sort is client-side (instant); game/period refetch.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  Pressable,
  ScrollView,
  Image,
  ActivityIndicator,
  StyleSheet,
  Platform,
} from 'react-native';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import FounderBadge from '../components/FounderBadge';
import useCritsEnabled from '../hooks/useCritsEnabled';

const LOGO = require('../../assets/logoIcon4.webp');

const SORTS = [
  { key: 'crits', label: 'Crits Earned' },
  { key: 'wins', label: 'Wins' },
  { key: 'podiums', label: 'Podiums' },
  { key: 'events', label: 'Events Entered' },
];

const AVATAR_COLORS = ['#FCD000', '#00bfff', '#b06cff', '#4ade80', '#ff5c8a', '#f59e0b'];
function colorFor(uid) {
  let h = 0;
  for (let i = 0; i < (uid || '').length; i++) h = (h * 31 + uid.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function initials(name) {
  return String(name || '?').replace(/[^A-Za-z0-9]/g, '').slice(0, 2).toUpperCase() || '?';
}

export default function LeaderboardScreen({ navigation }) {
  const [game, setGame] = useState('all');
  const [period, setPeriod] = useState('all');
  const [sort, setSort] = useState('crits');
  const [gamesList, setGamesList] = useState([]); // [{id,name}] for the tabs
  const [entries, setEntries] = useState([]);
  const [seasonNumber, setSeasonNumber] = useState(0);
  const [loading, setLoading] = useState(true);
  // Economy flag (evergreen step 2.3): with Crits off, the Crits column and
  // sort chip go away and the board ranks by wins. The hook starts TRUE and
  // flips on snapshot, so the re-home lives in an effect, not the default.
  const critsEnabled = useCritsEnabled();
  const sorts = useMemo(
    () => (critsEnabled ? SORTS : SORTS.filter((s) => s.key !== 'crits')),
    [critsEnabled]
  );
  useEffect(() => {
    if (!critsEnabled && sort === 'crits') setSort('wins');
  }, [critsEnabled, sort]);
  // No CritCash season while Crits are off: the board is all-time only.
  useEffect(() => {
    if (!critsEnabled && period !== 'all') setPeriod('all');
  }, [critsEnabled, period]);

  const load = useCallback(async (g, p) => {
    setLoading(true);
    try {
      const fn = httpsCallable(getFunctions(), 'getLeaderboard');
      const { data } = await fn({ game: g, period: p });
      setEntries(Array.isArray(data?.entries) ? data.entries : []);
      setSeasonNumber(data?.seasonNumber || 0);
      // Keep the full tab list from an "all" fetch; per-game fetches only return
      // the single game, so don't clobber the tabs with that.
      if (g === 'all' && Array.isArray(data?.games)) setGamesList(data.games);
    } catch (e) {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(game, period); }, [game, period, load]);

  const tabs = useMemo(() => [{ id: 'all', name: 'All Games' }, ...gamesList], [gamesList]);

  const ranked = useMemo(() => {
    const arr = entries.slice().sort((a, b) => (Number(b[sort]) || 0) - (Number(a[sort]) || 0));
    return arr.slice(0, 100);
  }, [entries, sort]);

  const currentGameName = (tabs.find((t) => t.id === game) || {}).name || 'All Games';
  const sortLabel = (sorts.find((s) => s.key === sort) || {}).label;

  return (
    <View style={styles.screen}>
      <Header navigation={navigation} />
      <ScrollView style={{ flex: 1 }} contentContainerStyle={styles.scroll} showsVerticalScrollIndicator={false}>
        <View style={styles.card}>
          {/* Header */}
          <View style={styles.top}>
            {navigation && (
              <Pressable
                onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Lobby'))}
                style={styles.backBtn}
              >
                <Text style={styles.backLabel}>BACK</Text>
              </Pressable>
            )}
            <Image source={LOGO} style={styles.logo} resizeMode="contain" />
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={styles.title}>
                CRIT<Text style={{ color: '#FCD000' }}>CASH</Text> LEADERBOARD
              </Text>
              <Text style={styles.sub} numberOfLines={1}>
                {currentGameName} · {period === 'all' ? 'All-Time' : (seasonNumber ? `Season ${seasonNumber}` : 'This Season')} · ranked by {sortLabel}
              </Text>
            </View>
            {critsEnabled && (
            <View style={styles.periodWrap}>
              {[['season', 'This Season'], ['all', 'All-Time']].map(([p, lbl]) => (
                <Pressable key={p} onPress={() => setPeriod(p)} style={[styles.periodBtn, period === p && styles.periodBtnOn]}>
                  <Text style={[styles.periodTxt, period === p && styles.periodTxtOn]}>{lbl}</Text>
                </Pressable>
              ))}
            </View>
            )}
          </View>

          {/* Game tabs */}
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.games}>
            {tabs.map((t) => (
              <Pressable key={t.id} onPress={() => setGame(t.id)} style={[styles.gameTab, game === t.id && styles.gameTabOn]}>
                <Text style={[styles.gameTxt, game === t.id && styles.gameTxtOn]}>{t.name}</Text>
              </Pressable>
            ))}
          </ScrollView>

          {/* Sort chips */}
          <View style={styles.sortRow}>
            <Text style={styles.sortLbl}>SORT BY</Text>
            {sorts.map((s) => (
              <Pressable key={s.key} onPress={() => setSort(s.key)} style={[styles.sortChip, sort === s.key && styles.sortChipOn]}>
                <Text style={[styles.sortChipTxt, sort === s.key && styles.sortChipTxtOn]}>{s.label}</Text>
              </Pressable>
            ))}
          </View>

          {/* Column header */}
          <View style={[styles.row, styles.headRow]}>
            <Text style={[styles.hRank]}>#</Text>
            <Text style={styles.hPlayer}>PLAYER</Text>
            {critsEnabled ? <Text style={[styles.hNum, sort === 'crits' && styles.hNumOn]}>CRITS</Text> : null}
            <Text style={[styles.hNum, sort === 'wins' && styles.hNumOn]}>WINS</Text>
            <Text style={styles.hNum}>LOSSES</Text>
            <Text style={[styles.hNum, sort === 'podiums' && styles.hNumOn]}>PODIUMS</Text>
            <Text style={[styles.hNum, sort === 'events' && styles.hNumOn]}>EVENTS</Text>
          </View>

          {/* Rows */}
          {loading ? (
            <View style={styles.empty}><ActivityIndicator color="#FCD000" /></View>
          ) : ranked.length === 0 ? (
            <View style={styles.empty}>
              <Text style={styles.emptyTxt}>No ranked players yet for this board.</Text>
              <Text style={styles.emptySub}>Compete in events to climb the ladder.</Text>
            </View>
          ) : (
            ranked.map((e, i) => {
              const rank = i + 1;
              const rankColor = rank === 1 ? '#FCD000' : rank === 2 ? '#cfd3da' : rank === 3 ? '#d8924a' : '#6a6a74';
              return (
                <Pressable
                  key={e.uid}
                  style={[styles.row, rank <= 3 && styles.rowTop]}
                  onPress={() => navigation && e.name && navigation.navigate('PublicProfile', { userName: e.name })}
                >
                  <Text style={[styles.rank, { color: rankColor }]}>{rank}</Text>
                  <View style={styles.player}>
                    <FounderBadge size={30} box={30} active={e.profileBadge === 'founder'}>
                      {e.photoUrl ? (
                        <Image source={{ uri: e.photoUrl }} style={styles.avatar} />
                      ) : (
                        <View style={[styles.avatar, styles.avatarInit, { backgroundColor: colorFor(e.uid) }]}>
                          <Text style={styles.avatarTxt}>{initials(e.name)}</Text>
                        </View>
                      )}
                    </FounderBadge>
                    <Text style={styles.name} numberOfLines={1}>{e.name}</Text>
                    {rank === 1 && <Image source={LOGO} style={styles.champ} resizeMode="contain" />}
                  </View>
                  {critsEnabled ? <Text style={[styles.num, styles.crit, sort === 'crits' && styles.numOn]}>{(e.crits || 0).toLocaleString()}</Text> : null}
                  <Text style={[styles.num, styles.win, sort === 'wins' && styles.numOn]}>{e.wins || 0}</Text>
                  <Text style={[styles.num, styles.loss]}>{e.losses || 0}</Text>
                  <Text style={[styles.num, sort === 'podiums' && styles.numOn]}>{e.podiums || 0}</Text>
                  <Text style={[styles.num, sort === 'events' && styles.numOn]}>{e.events || 0}</Text>
                </Pressable>
              );
            })
          )}

          <Text style={styles.foot}>
            Wins &amp; losses across head-to-heads, showdowns, brackets &amp; all events · verified players only · top 100
          </Text>
        </View>
      </ScrollView>

      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: '#06070a' },
  scroll: { padding: Platform.OS === 'web' ? 24 : 12, alignItems: 'center' },
  card: {
    width: '100%', maxWidth: 760, backgroundColor: '#0b0b0e',
    borderWidth: 1, borderColor: '#1f1f25', borderRadius: 16, overflow: 'hidden',
  },

  top: { flexDirection: 'row', alignItems: 'center', gap: 11, padding: 16, paddingBottom: 10, flexWrap: 'wrap' },
  backBtn: { paddingHorizontal: 11, paddingVertical: 7, borderRadius: 16, backgroundColor: 'rgba(255,255,255,0.06)' },
  backLabel: { color: '#FCD000', fontSize: 11, fontWeight: '800', letterSpacing: 0.5 },
  logo: { width: 30, height: 38 },
  title: { fontSize: 18, fontWeight: '900', letterSpacing: 1, color: '#fff' },
  sub: { fontSize: 11, color: '#7a7a85', marginTop: 2 },
  periodWrap: { flexDirection: 'row', backgroundColor: '#16161c', borderWidth: 1, borderColor: '#26262e', borderRadius: 9, overflow: 'hidden' },
  periodBtn: { paddingHorizontal: 11, paddingVertical: 6 },
  periodBtnOn: { backgroundColor: '#FCD000' },
  periodTxt: { fontSize: 11, fontWeight: '700', color: '#9a9aa5' },
  periodTxtOn: { color: '#000' },

  games: { gap: 7, paddingHorizontal: 16, paddingBottom: 10 },
  gameTab: { backgroundColor: '#16161c', borderWidth: 1, borderColor: '#26262e', borderRadius: 20, paddingHorizontal: 13, paddingVertical: 6, marginRight: 7 },
  gameTabOn: { backgroundColor: '#0a2230', borderColor: '#00bfff' },
  gameTxt: { fontSize: 12, fontWeight: '700', color: '#b6b6c0' },
  gameTxtOn: { color: '#00bfff' },

  sortRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 16, paddingBottom: 12, flexWrap: 'wrap' },
  sortLbl: { fontSize: 10, fontWeight: '800', letterSpacing: 0.5, color: '#666' },
  sortChip: { backgroundColor: '#16161c', borderWidth: 1, borderColor: '#26262e', borderRadius: 8, paddingHorizontal: 11, paddingVertical: 6 },
  sortChipOn: { backgroundColor: '#2a2200', borderColor: '#FCD000' },
  sortChipTxt: { fontSize: 12, fontWeight: '700', color: '#b6b6c0' },
  sortChipTxtOn: { color: '#FCD000' },

  row: { flexDirection: 'row', alignItems: 'center', paddingVertical: 9, paddingHorizontal: 16, borderBottomWidth: 1, borderBottomColor: '#141418' },
  rowTop: { backgroundColor: 'rgba(252,208,0,0.05)' },
  headRow: { borderTopWidth: 1, borderTopColor: '#1c1c22', borderBottomColor: '#1c1c22', paddingVertical: 8 },
  hRank: { width: 28, textAlign: 'center', fontSize: 10, fontWeight: '800', color: '#5e5e68' },
  hPlayer: { flex: 1, fontSize: 10, fontWeight: '800', color: '#5e5e68', marginLeft: 8 },
  hNum: { width: 56, textAlign: 'right', fontSize: 9.5, fontWeight: '800', color: '#5e5e68' },
  hNumOn: { color: '#FCD000' },

  rank: { width: 28, textAlign: 'center', fontSize: 15, fontWeight: '900' },
  player: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 9, marginLeft: 8, minWidth: 0 },
  avatar: { width: 30, height: 30, borderRadius: 15, backgroundColor: '#222' },
  avatarInit: { alignItems: 'center', justifyContent: 'center' },
  avatarTxt: { fontSize: 11, fontWeight: '800', color: '#000' },
  name: { flexShrink: 1, fontSize: 13, fontWeight: '700', color: '#ececf0' },
  champ: { width: 16, height: 20, marginLeft: 2 },

  num: { width: 56, textAlign: 'right', fontSize: 13, fontWeight: '700', color: '#aeb0b8' },
  numOn: { color: '#FCD000', fontWeight: '900' },
  crit: { color: '#d8b400' },
  win: { color: '#4ade80' },
  loss: { color: '#ff6b6b' },

  empty: { paddingVertical: 40, alignItems: 'center' },
  emptyTxt: { color: '#9a9aa5', fontSize: 13, fontWeight: '700' },
  emptySub: { color: '#5a5a64', fontSize: 11, marginTop: 4 },
  foot: { padding: 12, fontSize: 10, color: '#5a5a64', textAlign: 'center', borderTopWidth: 1, borderTopColor: '#1c1c22' },
});
